/**
 * DocumentViewer — Universal file viewer component.
 *
 * Auto-detects file type and renders the appropriate viewer:
 * - PDF: embedded iframe viewer
 * - Images: <img> with zoom controls
 * - Text/Code/CSV/JSON/YAML/XML/MD: syntax-highlighted text viewer
 * - DOCX: converted to HTML via mammoth.js
 * - XLSX: rendered as HTML table via SheetJS
 * - Audio/Video: HTML5 media player
 * - Unsupported: download prompt
 *
 * Usage:
 *   <DocumentViewer
 *     url="/api/v1/knowledge/documents/:id/file"
 *     fileName="manual.pdf"
 *     mimeType="application/pdf"
 *   />
 */
import { useState, useEffect, useCallback } from 'react';
import { sanitizeHtml } from '@/lib/sanitize';
import {
  Download, ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2,
  FileText, Image as ImageIcon, FileSpreadsheet, FileCode, Film, Music,
  Loader2, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
// L1 — for .md files reuse the rich chat renderer so Mermaid diagrams,
// tables, code-block syntax highlighting, and LaTeX render the same way
// they do inside Ask Argus replies. The previous viewer printed raw
// markdown source as preformatted text, which made any uploaded
// architecture / runbook .md unreadable in dark mode and rendered
// Mermaid blocks as code text rather than diagrams.
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
// Scoped DOCX/Markdown styling that honors light + dark design tokens.
// Replaces `prose` / `dark:prose-invert` which produced unreadable
// dark-on-dark text in dark mode (UI bug 2026-04-29).
import './document-viewer.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DocumentViewerProps {
  /** URL to fetch the file from */
  url: string;
  /** Original file name */
  fileName: string;
  /** MIME type */
  mimeType: string;
  /** Optional class for the container */
  className?: string;
  /** Height of the viewer. Default: '70vh' */
  height?: string;
}

type ViewerType = 'pdf' | 'image' | 'text' | 'markdown' | 'docx' | 'xlsx' | 'pptx' | 'media-video' | 'media-audio' | 'unsupported';

// ─── Type Detection ──────────────────────────────────────────────────────────

function detectViewerType(mimeType: string, fileName: string): ViewerType {
  const mime = mimeType.toLowerCase();
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';

  // PDF
  if (mime.includes('pdf') || ext === 'pdf') return 'pdf';

  // Images
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(ext)) return 'image';

  // Markdown — check first so .md isn't accidentally claimed by the
  // text branch when the stored MIME is `application/octet-stream`
  // (uploaded .md files commonly have no proper MIME).
  if (ext === 'md' || ext === 'markdown' || mime === 'text/markdown' || mime === 'text/x-markdown') {
    return 'markdown';
  }

  // Text-based (code, CSV, JSON, etc.). The extension list is what
  // catches files served with `application/octet-stream` MIME.
  // L1 — added 'md'/'markdown' as a safety net for the predicate
  // condition; the explicit return above already handles it but
  // including them here keeps the list readable as "text-y exts".
  if (
    mime.startsWith('text/') ||
    ['txt', 'csv', 'json', 'yaml', 'yml', 'xml', 'log', 'ini', 'cfg', 'conf', 'sh', 'bash', 'py', 'js', 'ts', 'html', 'css', 'sql', 'md', 'markdown'].includes(ext)
  ) {
    return 'text';
  }

  // Word documents
  if (mime.includes('wordprocessingml') || mime.includes('msword') || ext === 'docx' || ext === 'doc') return 'docx';

  // Excel
  if (mime.includes('spreadsheetml') || mime.includes('ms-excel') || ext === 'xlsx' || ext === 'xls') return 'xlsx';

  // PowerPoint
  if (mime.includes('presentationml') || mime.includes('ms-powerpoint') || ext === 'pptx' || ext === 'ppt') return 'pptx';

  // Video
  if (mime.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext)) return 'media-video';

  // Audio
  if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'].includes(ext)) return 'media-audio';

  return 'unsupported';
}

function viewerIcon(type: ViewerType) {
  switch (type) {
    case 'pdf': case 'docx': case 'text': case 'markdown': return <FileText className="h-5 w-5" />;
    case 'image': return <ImageIcon className="h-5 w-5" />;
    case 'xlsx': return <FileSpreadsheet className="h-5 w-5" />;
    case 'media-video': return <Film className="h-5 w-5" />;
    case 'media-audio': return <Music className="h-5 w-5" />;
    default: return <FileCode className="h-5 w-5" />;
  }
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function DocumentViewer({ url, fileName, mimeType, className, height = '70vh' }: DocumentViewerProps) {
  const viewerType = detectViewerType(mimeType, fileName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Blob URL for authenticated file access (PDF, images, media)
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Image zoom state
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Text/DOCX/XLSX/PPTX content state
  const [textContent, setTextContent] = useState<string>('');
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [tableData, setTableData] = useState<string[][]>([]);
  const [slides, setSlides] = useState<{ index: number; texts: string[] }[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Authenticated fetch helper
  const authFetch = useCallback((fileUrl: string) => {
    const token = localStorage.getItem('accessToken');
    return fetch(fileUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  }, []);

  // For PDF, images, media — fetch with auth token, create blob URL
  useEffect(() => {
    if (['pdf', 'image', 'media-video', 'media-audio'].includes(viewerType)) {
      setLoading(true);
      authFetch(url)
        .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.blob(); })
        .then(blob => { setBlobUrl(URL.createObjectURL(blob)); setLoading(false); })
        .catch(() => { setError('Failed to load file'); setLoading(false); });

      return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    }
    // For types that handle their own fetching, just mark as not loading
    if (!['text', 'markdown', 'docx', 'xlsx', 'pptx'].includes(viewerType)) {
      setLoading(false);
    }
  }, [url, viewerType]);

  // Fetch and convert content for text/docx/xlsx/pptx
  useEffect(() => {
    if (viewerType === 'text' || viewerType === 'markdown') {
      setLoading(true);
      authFetch(url)
        .then(r => r.text())
        .then(text => { setTextContent(text); setLoading(false); })
        .catch(() => { setError('Failed to load file'); setLoading(false); });
    }

    if (viewerType === 'docx') {
      setLoading(true);
      authFetch(url)
        .then(r => r.arrayBuffer())
        .then(async (buffer) => {
          const mammoth = await import('mammoth');
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          setHtmlContent(result.value);
          setLoading(false);
        })
        .catch(() => { setError('Failed to convert Word document'); setLoading(false); });
    }

    if (viewerType === 'xlsx') {
      setLoading(true);
      authFetch(url)
        .then(r => r.arrayBuffer())
        .then(async (buffer) => {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(buffer, { type: 'array' });
          const firstSheet = wb.Sheets[wb.SheetNames[0]];
          if (firstSheet) {
            const data = XLSX.utils.sheet_to_json<string[]>(firstSheet, { header: 1 }) as string[][];
            setTableData(data);
          }
          setLoading(false);
        })
        .catch(() => { setError('Failed to parse spreadsheet'); setLoading(false); });
    }

    if (viewerType === 'pptx') {
      setLoading(true);
      authFetch(url)
        .then(r => r.arrayBuffer())
        .then(async (buffer) => {
          const JSZip = (await import('jszip')).default;
          const zip = await JSZip.loadAsync(buffer);
          const slideFiles = Object.keys(zip.files).filter(f => f.match(/ppt\/slides\/slide\d+\.xml$/)).sort();
          const parsedSlides: { index: number; texts: string[] }[] = [];
          for (let i = 0; i < slideFiles.length; i++) {
            const xml = await zip.files[slideFiles[i]].async('text');
            // Extract text content from XML (simple regex approach)
            const texts = [...xml.matchAll(/<a:t>([^<]*)<\/a:t>/g)].map(m => m[1]).filter(t => t.trim());
            parsedSlides.push({ index: i + 1, texts });
          }
          setSlides(parsedSlides);
          setLoading(false);
        })
        .catch(() => { setError('Failed to parse presentation'); setLoading(false); });
    }
  }, [url, viewerType]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // #709 — Honour the server's Content-Disposition filename when
      // present. The knowledge file route prefixes the filename with
      // the document's classification marking (e.g. `[CUI]_manual.pdf`)
      // so the saved bytes carry their marking even after leaving the
      // platform. Pre-fix we forced `a.download = fileName`, which
      // stripped the prefix and produced an unmarked file on disk.
      const cd = res.headers.get('Content-Disposition') ?? '';
      const cdMatch = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const serverFileName = cdMatch?.[1];

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = serverFileName || fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab (may fail for auth-required endpoints)
      window.open(url, '_blank');
    }
  };

  // ── Loading / Error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={cn('flex items-center justify-center bg-muted/30 rounded-lg', className)} style={{ height }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('flex items-center justify-center bg-muted/30 rounded-lg', className)} style={{ height }}>
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />Download Instead
          </Button>
        </div>
      </div>
    );
  }

  // ── Viewer Toolbar ────────────────────────────────────────────────────────
  const toolbar = (
    <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/30 text-xs">
      <div className="flex items-center gap-2">
        {viewerIcon(viewerType)}
        <span className="font-medium truncate max-w-[300px]">{fileName}</span>
        <Badge variant="outline" className="text-[9px] uppercase">{viewerType}</Badge>
      </div>
      <div className="flex items-center gap-1">
        {viewerType === 'image' && (
          <>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} aria-label="Zoom out" title="Zoom out"><ZoomOut className="h-3.5 w-3.5" /></Button>
            <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.min(4, z + 0.25))} aria-label="Zoom in" title="Zoom in"><ZoomIn className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRotation(r => (r + 90) % 360)} aria-label="Rotate" title="Rotate"><RotateCw className="h-3.5 w-3.5" /></Button>
          </>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDownload} aria-label="Download" title="Download"><Download className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );

  // ── Render by type ────────────────────────────────────────────────────────
  return (
    <div className={cn('rounded-lg border overflow-hidden bg-card', className)}>
      {toolbar}

      <div style={{ height }} className="overflow-auto">
        {/* PDF */}
        {viewerType === 'pdf' && blobUrl && (
          <iframe src={blobUrl} className="w-full h-full border-0" title={fileName} />
        )}

        {/* Image */}
        {viewerType === 'image' && blobUrl && (
          <div className="flex items-center justify-center h-full bg-[#f0f0f0] dark:bg-[#1a1a2e] overflow-auto p-4">
            <img
              src={blobUrl}
              alt={fileName}
              className="transition-transform duration-200"
              style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, maxWidth: 'none' }}
            />
          </div>
        )}

        {/* Text / Code */}
        {viewerType === 'text' && (
          <pre className="p-4 text-sm font-mono whitespace-pre-wrap break-words bg-card text-foreground overflow-auto h-full">
            {textContent}
          </pre>
        )}

        {/* Markdown — rendered with the same MarkdownRenderer the chat
            uses, so headings, lists, tables, code-block syntax
            highlighting, LaTeX, and Mermaid diagrams all match the
            Ask Argus experience. L1 — earlier behaviour was a raw
            <pre> dump of source text which broke Mermaid diagrams
            and made tables unreadable. */}
        {viewerType === 'markdown' && (
          <div className="docx-md-content max-w-none p-6 text-foreground bg-card">
            <MarkdownRenderer content={textContent} />
          </div>
        )}

        {/* DOCX (converted to HTML by mammoth) — rendered through the
            scoped `.docx-content` stylesheet so headings, paragraphs,
            tables, and lists honor --color-foreground / --color-card
            in BOTH light and dark mode. Was using `prose
            dark:prose-invert` which rendered dark-on-dark in dark
            mode (unreadable). Per UI audit 2026-04-29 follow-up. */}
        {viewerType === 'docx' && (
          <div
            className="docx-content max-w-none p-6 text-foreground bg-card"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
          />
        )}

        {/* XLSX (rendered as table). Cells use `text-foreground` so
            they remain readable in dark mode — the previous version
            inherited browser default black text which collided with
            the dark card surface. */}
        {viewerType === 'xlsx' && (
          <div className="overflow-auto h-full bg-card text-foreground">
            <table className="w-full text-sm border-collapse">
              <tbody>
                {tableData.map((row, ri) => (
                  <tr
                    key={ri}
                    className={cn(
                      ri === 0
                        ? 'bg-muted font-bold text-foreground'
                        : 'border-b border-border text-foreground hover:bg-muted/30',
                    )}
                  >
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="px-3 py-1.5 border-r border-border text-xs whitespace-nowrap text-foreground"
                      >
                        {String(cell ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {tableData.length === 0 && <p className="text-center py-8 text-muted-foreground">Empty spreadsheet</p>}
          </div>
        )}

        {/* Video */}
        {viewerType === 'media-video' && blobUrl && (
          <div className="flex items-center justify-center h-full bg-black">
            <video controls className="max-w-full max-h-full" src={blobUrl}><track kind="captions" /></video>
          </div>
        )}

        {/* Audio */}
        {viewerType === 'media-audio' && blobUrl && (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <Music className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm font-medium mb-4">{fileName}</p>
              <audio controls src={blobUrl} className="w-full max-w-md" />
            </div>
          </div>
        )}

        {/* PowerPoint */}
        {viewerType === 'pptx' && slides.length > 0 && (
          <div className="flex flex-col h-full">
            {/* Slide content area */}
            <div className="flex-1 flex items-center justify-center p-8 bg-[#f8f8f8] dark:bg-[#1a1a2e]">
              <div className="w-full max-w-3xl aspect-[16/9] bg-card text-foreground rounded-lg shadow-lg border p-8 flex flex-col justify-center">
                {slides[currentSlide]?.texts.map((text, i) => (
                  <p key={i} className={i === 0 ? 'text-2xl font-bold text-foreground mb-4' : 'text-base text-muted-foreground mb-2'}>{text}</p>
                ))}
                {slides[currentSlide]?.texts.length === 0 && (
                  <p className="text-center text-muted-foreground italic">Slide {currentSlide + 1} (no text content)</p>
                )}
              </div>
            </div>
            {/* Slide navigator */}
            <div className="flex items-center justify-center gap-3 py-3 border-t bg-muted/30">
              <Button variant="outline" size="sm" disabled={currentSlide <= 0} onClick={() => setCurrentSlide(s => s - 1)}>Previous</Button>
              <span className="text-sm font-medium">Slide {currentSlide + 1} of {slides.length}</span>
              <Button variant="outline" size="sm" disabled={currentSlide >= slides.length - 1} onClick={() => setCurrentSlide(s => s + 1)}>Next</Button>
            </div>
            {/* Slide thumbnails */}
            <div className="flex gap-2 px-4 py-2 border-t overflow-x-auto">
              {slides.map((slide, i) => (
                <button key={i} onClick={() => setCurrentSlide(i)}
                  className={cn('shrink-0 w-24 h-14 rounded border text-[8px] p-1 text-left overflow-hidden transition-all', i === currentSlide ? 'border-primary ring-2 ring-primary/30 bg-card' : 'border-muted bg-muted/30 hover:border-muted-foreground/30')}
                >
                  <p className="font-bold truncate">{slide.texts[0] ?? `Slide ${slide.index}`}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Unsupported */}
        {viewerType === 'unsupported' && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileCode className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-40" />
              <p className="text-sm font-medium">{fileName}</p>
              <p className="text-xs text-muted-foreground mt-1">Preview not available for this file type</p>
              <Badge variant="outline" className="mt-2 text-xs">{mimeType}</Badge>
              <div className="mt-4">
                <Button onClick={handleDownload}><Download className="h-4 w-4 mr-2" />Download File</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
